import { injectExtraActions } from './cz-components/cz-actions/fake-wizard.ts';
import { initMatomo } from './lib/analytics/matomo';
import { initAppInsights, initSentry } from './log';
import './types.ts';

/**
 * Opt out of loading default font
 * @see https://github.com/PolymerElements/font-roboto/blob/master/roboto.js
 */
window.polymerSkipLoadingFontRoboto = true;

window.cz = window.cz || {};

const queryParams = new URL(window.location).searchParams,
	stringToBool = (string) => string === 'true';

Object.assign(window.cz, {
	config: window.cz && window.cz.config,
	debug: {
		// this is a getter to make it easier to toggle for testers instead of an assignment or method call
		get otsShowLocalHeaders() {
			document.getElementById('start').updateStyles({
				'--cosmoz-omnitable-local-filter-header-color': 'red',
			});
			return '🔦 Watch out for red Omnitable headers! 😱';
		},
	},
	options: {
		// custom auth providers can override this with query params
		idp: null,
		// backend base URI for any ajax requests
		backendBaseUri: undefined,
		frontendBaseUri: window.czcore.frontendBaseUri,
		googleClientId: undefined,
		debug: true,
	},
	ui: {},
	state: {
		app: undefined,
		currentLocationPath: queryParams.get('cpl'),
		currentNode: {},
		loggedIn: false,
		myItemsOnly: stringToBool(queryParams.get('mio')),
		offline: false,
		offlineLastRetry: new Date(),
		start: window.cz && window.cz.state && window.cz.state.start,
		tree: undefined,
		unsavedWork: false,
	},
	workers: {},
});

const fetchOpts = {
	credentials: 'include',
	mode: 'cors',
	// eslint-disable-next-line no-undef
	...(process.env.NODE_ENV === 'development' && { cache: 'force-cache' }),
};

// Configure cosmoz-queue fetch wrapper with dynamic headers
import { setBaseInit } from '@neovici/cosmoz-queue/util/fetch';
setBaseInit({
	...fetchOpts,
	getHeaders: () => {
		const cpl = window.cz?.state?.currentLocationPath;
		return cpl ? { 'cz-path-locator': cpl } : {};
	},
});

const json$ = (url) => fetch(url).then((r) => r.json());

Promise.all([json$('cz.config.json'), json$('cz.version.json')]).then(
	async ([config, version]) => {
		if (config.azureConnectionString) {
			initAppInsights(config.azureConnectionString);
		}
		if (config.sentry?.dsn) {
			initSentry({ ...config.sentry, ...version });
		}
		if (config.matomo) {
			initMatomo(config.matomo);
		}
		Object.assign(window.cz, { version });
		Object.assign(window.cz, { config });

		window.czTrackerConfig = {
			endpoint: config.loseUrl ? `${config.loseUrl}/api/v1/events` : undefined,
		};

		// Imported after cz.config is assigned: redirect-param pulls in
		// #cz/config, whose module-eval-time env/isProd/isBeta constants
		// must not be captured before the config is in place.
		const { checkstatusParams } = await import('./lib/routing/redirect-param');
		const getApiCall = (call) => {
				return new URL(`${config.backendBaseUri}${call}`);
			},
			statusApiUrl = getApiCall('api/Auth/CheckStatus'),
			initClient = getApiCall(
				'api/v2/Boot/InitClient?includeOrganizations=false',
			),
			start = document.getElementById('start'),
			{ returnUrl, ...statusApiParams } = checkstatusParams();

		window.czcore.setUrlParams(statusApiUrl, statusApiParams);

		fetch(statusApiUrl, {
			...fetchOpts,
			body: JSON.stringify(returnUrl),
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
		})
			.then(async (response) => {
				const authStatus = await response.json();

				// Redirect immediately if this is required by specified authentication provider
				if (authStatus.redirectUri) {
					window.location.href = authStatus.redirectUri;
					return;
				}

				if (response.ok) {
					start.language = authStatus.language;
					const response = await fetch(initClient, fetchOpts);
					const boot = await response.json();
					injectExtraActions(boot);
					czcore.boot.set(boot);
				} // cz-start hasn't been upgraded yet, but _authStatus will survive and become default value anyway
				start._authStatus = authStatus;
				import('./start.js');
			})
			.catch((error) => {
				start._authStatus = {
					authenticated: false,
					message: error.message,
				};
			});

		window.dispatchEvent(new CustomEvent('cz-config-loaded'));
	},
);

czcore.boot.increaseProgress(10, 'Core boot loaded');
czcore.boot.incLoop();
